import { useCallback, useMemo } from 'react';

import { Dropdown, Popconfirm, Space, type MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CloseIcon } from '../../../Icons';
import { WhiteButton } from '../../../Button';

// Utils
import { useTranslation } from 'react-i18next';

// Redux
import { uiActions } from '../../../../store/slices/ui';
import { authActions, loginToSpotify } from '../../../../store/slices/auth';
import { feedbackActions } from '../../../../store/slices/feedback';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { libraryCache } from '../../../../lib/cache/libraryCache';
import { clearAllCaches } from '../../../../lib/cache/clearAllCaches';
import { isDevUser } from '../../../../lib/config/devUsers';

// Constants
import { ARTISTS_DEFAULT_IMAGE } from '../../../../constants/spotify';

const LoginButton = () => {
  const { t } = useTranslation(['home']);
  const dispatch = useAppDispatch();
  const tooltipOpen = useAppSelector((state) => state.ui.loginButtonOpen);

  const onClose = useCallback(() => {
    dispatch(uiActions.closeLoginButton());
  }, [dispatch]);

  return (
    <Popconfirm
      icon={null}
      open={tooltipOpen}
      onCancel={onClose}
      placement='bottomLeft'
      rootClassName='login-tooltip'
      cancelText={<CloseIcon />}
      title={t('You’re logged out')}
      cancelButtonProps={{ type: 'text' }}
      okButtonProps={{ className: 'white-button small' }}
      description={t('Log in to add this to your Liked Songs.')}
    >
      <WhiteButton title={t('Log In')} onClick={() => dispatch(loginToSpotify(false))} />
    </Popconfirm>
  );
};

const Header = ({ opacity }: { opacity: number; title?: string }) => {
  const { t } = useTranslation(['navbar']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector(
    (state) => state.auth.user,
    (prev, next) => prev?.id === next?.id
  );

  const isDev = useMemo(() => isDevUser(user?.id ?? null), [user?.id]);

  const userMenuItems = useMemo<MenuProps['items']>(() => {
    const items: MenuProps['items'] = [
      {
        key: 'profile',
        label: t('Profile'),
      },
      {
        key: 'settings',
        label: t('Settings'),
      },
      {
        key: 'how-it-works',
        label: 'How it works',
      },
      {
        key: 'feedback',
        label: t('Give feedback'),
      },
    ];

    if (isDev) {
      items.push({
        key: 'clear-cache',
        label: 'Clear cache (dev)',
      });
    }

    items.push({ type: 'divider' });
    items.push({
      key: 'logout',
      label: t('Log out'),
    });
    return items;
  }, [t, isDev]);

  const handleUserMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (!user) return;

      switch (key) {
        case 'profile':
          navigate(`/users/${user.id}`);
          break;
        case 'settings':
          navigate('/settings/library-sync');
          break;
        case 'feedback':
          dispatch(feedbackActions.openModal());
          break;
        case 'how-it-works':
          navigate('/how-it-works');
          break;
        case 'clear-cache':
          (async () => {
            try {
              await libraryCache.clear(user.id);
              await clearAllCaches();
              // eslint-disable-next-line no-console
              console.info('[Cache] cleared caches', { userId: user.id });
              window.location.reload();
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('[Cache] failed to clear caches', error);
            }
          })();
          break;
        case 'logout':
          dispatch(authActions.removeUser());
          break;
        default:
          break;
      }
    },
    [dispatch, navigate, user]
  );

  return (
    <div
      className={`flex r-0 w-full flex-row items-center justify-between bg-gray-900 rounded-t-md z-10`}
      style={{ backgroundColor: `rgba(12, 12, 12, ${opacity}%)` }}
    >
      <div className='flex flex-row items-center'>
        <Space>
          <button
            type='button'
            className='how-it-works-nav'
            onClick={() => navigate('/how-it-works')}
          >
            How it works
          </button>

          {/*
          <div className='news'>
            <News />
          </div> */}

          {user ? (
            <Dropdown
              placement='bottomRight'
              trigger={['click']}
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            >
              <div className='avatar-container'>
                <img
                  className='avatar'
                  id='user-avatar'
                  alt='User Avatar'
                  style={{ marginTop: -1 }}
                  src={
                    user?.images && user.images.length
                      ? user.images[0].url
                      : ARTISTS_DEFAULT_IMAGE
                  }
                />
              </div>
            </Dropdown>
          ) : (
            <LoginButton />
          )}
        </Space>
      </div>
    </div>
  );
};

export default Header;
